import { isCustomFieldValueBlank, validateCustomFieldValue } from "@salestrak/custom-fields";

import { checkedAnswer, isFieldItem, isFillableItem, isPhotoItem, resolveItemField, withItemRules } from "./items.ts";
import type { FormField, FormItem, FormSubmission } from "./types.ts";

/** Key order is not data: jsonb hands objects back with its own. */
function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, entry]) => entry !== undefined)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, entry]) => [key, canonical(entry)]),
    );
  }
  return value;
}

export function sameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
}

export function isPrefilledAnswer(prefilled: Record<string, unknown> | null | undefined, itemId: string, value: unknown): boolean {
  return prefilled != null && itemId in prefilled && sameJson(prefilled[itemId], value);
}

/** Whether the filler gave anything. A blank or untouched-prefill filling is
 *  no filling: it is never stored. */
export function hasSubmissionAnswers(answers: Record<string, unknown>, prefilled?: Record<string, unknown> | null): boolean {
  return Object.entries(answers).some(
    ([itemId, value]) => !isCustomFieldValueBlank(value) && !isPrefilledAnswer(prefilled, itemId, value),
  );
}

/** itemId → message, for the answers Submit will not accept. */
export type SubmissionErrors = Record<string, string>;

/** Pass the walked items: a question a condition held off is never held
 *  against the filler. */
export function validateSubmission<F extends FormField>(
  items: readonly FormItem<F>[],
  answers: Record<string, unknown>,
  catalog: ReadonlyMap<string, F>,
  timeZone?: string,
): SubmissionErrors {
  const errors: SubmissionErrors = {};
  for (const item of items) {
    if (!isFieldItem(item) || !isFillableItem(item, catalog)) continue;
    const field = resolveItemField(item, catalog);
    if (!field) continue;
    const value = answers[item.itemId];
    if (isCustomFieldValueBlank(value)) {
      if (item.required && field.type !== "checkbox") errors[item.itemId] = "Required";
      continue;
    }
    const result = validateCustomFieldValue(withItemRules(item, field), checkedAnswer(field.type, value), timeZone);
    if (!result.valid) errors[item.itemId] = result.message ?? "Invalid value";
  }
  return errors;
}

export function stringIds(answer: unknown): string[] {
  return Array.isArray(answer) ? answer.filter((id): id is string => typeof id === "string") : [];
}

/** One answer's photos that still exist. */
export function livePhotoIds(filling: Pick<FormSubmission, "answers" | "deletedPhotoIds">, itemId: string): string[] {
  const deleted = filling.deletedPhotoIds ?? [];
  return stringIds(filling.answers[itemId]).filter((id) => !deleted.includes(id));
}

/** The answers with each deleted photo taken out: what Submit judges and
 *  writes to the Project. */
export function withoutDeletedPhotos<F extends FormField>(
  items: readonly FormItem<F>[],
  filling: Pick<FormSubmission, "answers" | "deletedPhotoIds">,
): Record<string, unknown> {
  if (!filling.deletedPhotoIds?.length) return filling.answers;
  const answers = { ...filling.answers };
  for (const item of items) {
    if (!isPhotoItem(item) || !(item.itemId in answers)) continue;
    const live = livePhotoIds(filling, item.itemId);
    if (live.length > 0) answers[item.itemId] = live;
    else delete answers[item.itemId];
  }
  return answers;
}

/** Every photo a filling holds: what deleting it deletes. */
export function submissionPhotoIds<F extends FormField>(
  items: readonly FormItem<F>[],
  filling: Pick<FormSubmission, "answers" | "deletedPhotoIds">,
): string[] {
  return items.flatMap((item) => (isPhotoItem(item) ? livePhotoIds(filling, item.itemId) : []));
}
